/**
 * 키마다 잡은 곳을 센다. 여러 화면이 같은 상태를 볼 때 마지막으로 놓는 곳만 그 상태를 정리하게 한다.
 *
 * 같은 Dataset 이 창과 route 페이지에 함께 열릴 수 있다. 한쪽을 닫을 때 다른 쪽이 보는 편집 상태를 버리면 안 된다.
 *
 * Owns:
 * - 잡기와 놓기의 짝(놓기는 한 번만 센다), 처음 잡을 때와 마지막으로 놓을 때의 알림.
 *
 * Does not own:
 * - 무엇을 만들고 버리는지 — 알림을 받는 쪽(레지스트리 · 편집 상태)이 정한다.
 */
export class Holders<K> {
  private readonly holders = new Map<K, Set<symbol>>();

  constructor(
    private readonly on: {
      /** 아무도 잡지 않던 키를 처음 잡았다. */
      readonly first?: (key: K) => void;
      /** 마지막으로 잡은 곳이 놓았다. */
      readonly last: (key: K) => void;
    },
  ) {}

  /** 키를 잡는다. 돌려준 함수가 놓는다. 여러 번 불러도 한 번만 놓는다. */
  hold(key: K): () => void {
    const token = Symbol();
    const current = this.holders.get(key) ?? new Set<symbol>();
    this.holders.set(key, current);
    current.add(token);
    if (current.size === 1) this.on.first?.(key);
    return () => {
      // `clear()` 뒤에 남은 놓기는 새로 잡은 곳을 세지 않는다 — 그 잡기는 이미 잊었다.
      const holders = this.holders.get(key);
      if (holders === undefined || !holders.delete(token) || holders.size > 0) return;
      this.holders.delete(key);
      this.on.last(key);
    };
  }

  /** 이 키를 잡은 곳의 수. */
  count(key: K): number {
    return this.holders.get(key)?.size ?? 0;
  }

  /** 모든 잡기를 잊는다. 알리지 않는다 — 상태를 통째로 버릴 때(세션 끝 · 테스트) 부른다. */
  clear(): void {
    this.holders.clear();
  }
}
